import React from 'react';
import { Space } from 'antd';
import styles from './NamedItemComponent.module.css';

/**
 * NamedItemComponent - Labeled Interactive Item
 * 
 * A reusable component for displaying labeled items with optional click handlers.
 * Commonly used for document links, resource buttons, and interactive educational elements.
 * 
 * The component provides a horizontal layout with:
 * - Optional label (name)
 * - Content area (children)
 * - Hover effects for interactivity
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {string} [props.name] - Optional label text displayed before the content
 * @param {Function} [props.onClick] - Optional click handler for the entire component
 * @param {React.ReactNode} props.children - Content to display (buttons, icons, text, etc.)
 * 
 * @returns {JSX.Element} Clickable labeled item with hover effects
 * 
 * @example
 * // Document link with label
 * <NamedItemComponent name="Reference Manual" onClick={() => openPDF('/docs/manual.pdf')}>
 *   <Button icon={<FileOutlined />}>View PDF</Button>
 * </NamedItemComponent>
 * 
 * @example
 * // Simple labeled button
 * <NamedItemComponent name="Exercise 1">
 *   <Button type="primary">Start</Button>
 * </NamedItemComponent>
 */
export function NamedItemComponent({ name, onClick, children }) {
  /**
   * Handles click events on the component
   * Only triggers if onClick handler is provided
   */
  const handleClick = () => {
    if (onClick) onClick();
  };

  return (
    <Space align="center" className={styles.container} onClick={handleClick}>
      {/* Optional label (only rendered if name is provided) */}
      {name && <div className={styles.label}>{name}</div>}
      
      {/* Content area for children elements */}
      <div className={styles.content}>{children}</div>
    </Space>
  );
}

export default NamedItemComponent;
